import { AxiosInstance } from "axios";
import { Directory, DirectoryConfigInput, DirectoryType, DomainControllerDiscovery } from "./Types";

const baseUrl = "/api/directories";

export async function listDirectories(req: AxiosInstance): Promise<Directory[]> {
    const resp = await req.get<Directory[]>(baseUrl);
    return resp.data || [];
}

export interface SaveDirectoryInput {
    id?: string
    type: DirectoryType
    name: string
    description: string
    config: DirectoryConfigInput
    // LDAP only: the forest partitions the admin checked. Two or more turn this
    // directory into a forest parent with one child row per domain; zero or one
    // leaves a single row
    domains?: string[]
    // LDAP forest domain rows only: the domain controller the domain is read
    // through. It can be changed but not cleared; undefined leaves it alone.
    domainController?: string
    // Undefined leaves the stored selection alone; an empty array means every
    // instance. Never sent for a forest domain row, which the API refuses.
    icapInstances?: string[]
    enabled?: boolean
    onCreated?: (id: string) => void
}

export async function saveDirectory(req: AxiosInstance, input: SaveDirectoryInput): Promise<Directory[]> {
    const domains = input.type === "ldap" ? (input.domains || []) : [];

    let parent: Directory;

    if (input.id === undefined) {
        const resp = await req.post<Directory>(baseUrl, {
            name: input.name,
            description: input.description,
            type: input.type,
            // Google has no credentials to submit at create time
            config: input.type === "google" ? undefined : input.config,
            enabled: input.enabled ?? false,
            icapInstances: input.icapInstances,
        });
        parent = resp.data;
        input.onCreated?.(parent.id);
    } else {
        const resp = await req.put<Directory>(`${baseUrl}/${input.id}`, {
            name: input.name,
            description: input.description,
            domainController: input.domainController,
            icapInstances: input.icapInstances,
        });
        parent = resp.data;
        if (input.type !== "google" && parent.parentId === undefined) {
            const updated = await req.post<Directory>(
                `${baseUrl}/${input.id}/${input.type}/integrate`,
                input.config,
            );
            parent = updated.data;
        }
    }

    let written = [parent];
    if (input.type === "ldap" && parent.parentId === undefined) {
        const resp = await req.put<Directory[]>(`${baseUrl}/${parent.id}/domains`, { domains });
        written = resp.data || [parent];
    }

    return applyEnabled(req, parent, written, input.enabled);
}

async function applyEnabled(
    req: AxiosInstance,
    parent: Directory,
    written: Directory[],
    enabled: boolean | undefined,
): Promise<Directory[]> {
    // A domain row is compared on its own switch: its `enabled` also reflects the forest's.
    const isDomainRow = parent.parentId !== undefined;
    const current = isDomainRow ? (parent.domainEnabled ?? parent.enabled) : parent.enabled;
    // A forest's own check is about the address setup uses; its domains have their
    // own, so a failed one doesn't stop it being switched on.
    const isForest = parent.type === "ldap" && !isDomainRow;
    if (enabled === undefined || current === enabled
        || (enabled && !isDomainRow && !isForest && !parent.connectionValidated)) {
        return written;
    }

    const updated = await setDirectoryEnabled(req, parent.id, enabled);
    return written.map((row) => row.id === updated.id ? updated : { ...row, enabled });
}

export async function deleteDirectory(req: AxiosInstance, id: string): Promise<void> {
    await req.delete(`${baseUrl}/${id}`);
}

export async function setDirectoryEnabled(req: AxiosInstance, id: string, enabled: boolean): Promise<Directory> {
    const resp = await req.put<Directory>(`${baseUrl}/${id}/enabled`, { enabled });
    return resp.data;
}

export interface GoogleConsentUrl {
    consentUrl: string
    credentialsName?: string
}

export interface GoogleDirectorySetup {
    callbackUrl: string
}

export async function getGoogleSetup(req: AxiosInstance): Promise<GoogleDirectorySetup> {
    const resp = await req.get<GoogleDirectorySetup>(`${baseUrl}/google/setup`);
    return resp.data;
}

export async function integrateGoogle(req: AxiosInstance, id: string, file: File): Promise<GoogleConsentUrl> {
    const form = new FormData();
    form.append("googleCredentials", file);

    const resp = await req.post<GoogleConsentUrl>(`${baseUrl}/${id}/google/integrate`, form, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return resp.data;
}

export async function discardGoogleCredentials(req: AxiosInstance, id: string): Promise<Directory> {
    const resp = await req.delete<Directory>(`${baseUrl}/${id}/google/pending`);
    return resp.data;
}

interface DirectoryDomainsResponse {
    domains: string[]
}

export async function testDirectoryConnection(req: AxiosInstance, input: {
    id?: string,
    type: DirectoryType,
    config?: DirectoryConfigInput,
}): Promise<void> {
    await req.post(`${baseUrl}/${input.id || input.type}/test-connection`, input.config ?? {});
}

export async function discoverLdapDomains(req: AxiosInstance, input: {
    id?: string,
    addr: string,
    username: string,
    password?: string,
}): Promise<string[]> {
    const resp = await req.post<DirectoryDomainsResponse>(`${baseUrl}/${input.id || "ldap"}/ldap/domains`, {
        addr: input.addr,
        username: input.username,
        password: input.password,
    });
    return resp.data.domains || [];
}

// Lists the domain controllers serving one forest domain. A forest domain row owns
// its domain, so `domain` is only needed for a standalone or forest-root directory.
export async function discoverLdapDomainControllers(req: AxiosInstance, input: {
    id: string,
    domain?: string,
}): Promise<DomainControllerDiscovery> {
    const resp = await req.post<DomainControllerDiscovery>(
        `${baseUrl}/${input.id}/ldap/domain-controllers`,
        { domain: input.domain },
    );
    return {
        ...resp.data,
        controllers: resp.data.controllers || [],
        configured: resp.data.configured || "",
    };
}
